import React from "react";
import { Bug } from "lucide-react";
import { ActiveInfectionCard } from "../../molecules/ActiveInfectionCard";
import { InfectionsResponse } from "../../../types/residents";

interface InfectionsSectionProps {
  data: InfectionsResponse | null;
  isLoading: boolean;
  error: Error | null;
  insightsDays?: number;
}

export const InfectionsSection: React.FC<InfectionsSectionProps> = ({
  data,
  isLoading,
  error,
  insightsDays,
}) => {
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          Loading infections...
        </div>
      );
    }

    if (error) {
      return (
        <div className="py-4 text-center text-destructive">
          Error loading infections: {error.message}
        </div>
      );
    }

    if (!data || data.infections.length === 0) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          No active infections
        </div>
      );
    }

    const activeInfections = data.infections.filter(
      (inf) => inf.status === "ACTIVE" || inf.status === "MONITORING"
    );

    if (activeInfections.length === 0) {
      return (
        <div className="py-4 text-center text-muted-foreground">
          No active infections
        </div>
      );
    }

    // Sort by onset_date descending (most recent first)
    const sorted = [...activeInfections].sort((a, b) => {
      if (!a.onset_date && !b.onset_date) return 0;
      if (!a.onset_date) return 1;
      if (!b.onset_date) return -1;
      return b.onset_date.localeCompare(a.onset_date);
    });

    // Determine cutoff for "new insight" tagging
    const cutoff = insightsDays
      ? new Date(Date.now() - insightsDays * 24 * 60 * 60 * 1000)
      : null;

    return (
      <div className="flex flex-col gap-4">
        {sorted.map((infection, index) => {
          const isNew =
            cutoff && infection.onset_date
              ? new Date(infection.onset_date) >= cutoff
              : false;
          return (
            <ActiveInfectionCard
              key={infection.id || index}
              infection={{
                name: infection.type,
                identifiedDate: infection.onset_date ?? "Unknown",
                isolation: infection.isolation,
                signs: infection.signs,
                tests: infection.tests,
                treatments: infection.treatments,
                inCarePlan: infection.in_care_plan,
              }}
              isNewInsight={isNew}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <Bug className="w-5 h-5 text-gray-700" />
        <h2 className="text-lg font-semibold text-gray-900">Active Infections</h2>
      </div>

      {renderContent()}
    </div>
  );
};
